'use client';

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { useToast } from './Toast';
import Card from './Card';

type UserPrefRow = {
  eventClass: string;
  app: boolean;
  email: boolean;
  defaultApp: boolean;
  defaultEmail: boolean;
};

const EVENT_CLASS_LABELS: Record<string, string> = {
  ASSIGNED: 'notifClassAssigned',
  ENTITY_UPDATE: 'notifClassEntityUpdate',
  WATCHED_UPDATE: 'notifClassWatchedUpdate',
  TEAM_UPDATE: 'notifClassTeamUpdate',
  CASE_REPLY: 'notifClassCaseReply',
};

export default function UserNotificationPrefs() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { data: rows, mutate } = useSWR<UserPrefRow[]>('/api/user/notifications/preferences');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const toggle = useCallback(async (row: UserPrefRow, channel: 'app' | 'email', value: boolean) => {
    const key = `${row.eventClass}.${channel}`;
    setSavingKey(key);
    // Optimistic update
    mutate(
      (rows ?? []).map((r) => (r.eventClass === row.eventClass ? { ...r, [channel]: value } : r)),
      false
    );
    try {
      await apiFetch('/api/user/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify({ prefs: { [row.eventClass]: { app: channel === 'app' ? value : row.app, email: channel === 'email' ? value : row.email } } }),
      });
      showToast(t('saved'), 'success');
    } catch (e: any) {
      mutate(); // revert
      showToast(e?.message || t('saveFailed'), 'error');
    } finally {
      setSavingKey(null);
    }
  }, [rows, mutate, showToast, t]);

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold text-bark mb-1">🔔 {t('notifUserPrefsTitle')}</h2>
      <p className="text-xs text-wood mb-4">{t('notifUserPrefsDesc')}</p>
      {!rows ? (
        <div className="text-sm text-wood">{t('loading')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-wheat">
                <th className="text-left py-2 pr-4 font-medium text-wood">{t('notifEvent')}</th>
                <th className="py-2 px-3 font-medium text-wood text-center w-24">{t('notifChannelApp')}</th>
                <th className="py-2 px-3 font-medium text-wood text-center w-24">{t('notifChannelEmail')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const saving = savingKey !== null && savingKey.startsWith(`${row.eventClass}.`);
                return (
                  <tr key={row.eventClass} className="border-b border-wheat/40 last:border-0">
                    <td className="py-2.5 pr-4 text-bark">
                      {t(EVENT_CLASS_LABELS[row.eventClass] ?? row.eventClass)}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={row.app}
                        disabled={saving}
                        onChange={(e) => toggle(row, 'app', e.target.checked)}
                        className="w-5 h-5 rounded border-wheat text-moss focus:ring-moss cursor-pointer"
                        title={t('notifChannelAppDesc')}
                      />
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={row.email}
                        disabled={saving}
                        onChange={(e) => toggle(row, 'email', e.target.checked)}
                        className="w-5 h-5 rounded border-wheat text-moss focus:ring-moss cursor-pointer"
                        title={t('notifChannelEmailDesc')}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
